import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import { useAsync } from '../lib/useAsync';
import { calendarApi } from '../api/resources';
import { PageLoader, ErrorState, EmptyState } from '../components/ui';
import { formatDate, formatDateTime, cx } from '../lib/format';

const VIEWS = ['month', 'week', 'agenda'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Build a YYYY-MM-DD key from a date's LOCAL components (avoids UTC day-shift). */
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function entryDate(e) {
  return new Date(e.starts_at || e.date || e.start || e.start_date);
}

/** Resolve a detail-page path for a calendar entry, or null for non-linkable entries. */
function entryLink(e) {
  if (!e.slug) return null;
  if (e.type === 'event') return `/events/${e.slug}`;
  if (e.type === 'activity') return `/activities/${e.slug}`;
  return null;
}

/** Wrap content in a router Link when the entry is linkable, otherwise render plain. */
function EntryWrapper({ entry, className, title, children }) {
  const to = entryLink(entry);
  if (to) {
    return (
      <Link to={to} className={className} title={title}>
        {children}
      </Link>
    );
  }
  return (
    <div className={className} title={title}>
      {children}
    </div>
  );
}

export default function CalendarPage() {
  const [view, setView] = useState('month');
  const [cursor, setCursor] = useState(new Date());
  const { data, loading, error, reload } = useAsync(() => calendarApi.list({ per_page: 100 }), []);

  const entries = useMemo(() => data?.items || [], [data]);

  const byDay = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      const start = entryDate(e);
      if (Number.isNaN(start.getTime())) return;
      const endRaw = e.ends_at ? new Date(e.ends_at) : start;
      const end = Number.isNaN(endRaw.getTime()) || endRaw < start ? start : endRaw;
      const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      let guard = 0;
      while (cur <= last && guard < 90) {
        (map[ymd(cur)] = map[ymd(cur)] || []).push(e);
        cur.setDate(cur.getDate() + 1);
        guard += 1;
      }
    });
    return map;
  }, [entries]);

  const monthGrid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  function shift(delta) {
    const d = new Date(cursor);
    if (view === 'week') d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setCursor(d);
  }

  const weekDays = useMemo(() => {
    const start = new Date(cursor);
    start.setDate(cursor.getDate() - cursor.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  return (
    <>
      <Seo title="Calendar" description="View upcoming events and important dates for Zonta District 17 Area 1." />
      <PageHero
        eyebrow="What's On"
        title="Calendar"
        subtitle="Events, meetings, and key dates at a glance."
        image="/img/give-heart.jpg"
      />

      <section className="container-page py-12">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} className="btn-ghost px-3 py-1.5">←</button>
            <span className="min-w-[180px] text-center font-display text-lg font-semibold text-maroon-700">
              {cursor.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button onClick={() => shift(1)} className="btn-ghost px-3 py-1.5">→</button>
            <button onClick={() => setCursor(new Date())} className="btn-outline px-3 py-1.5 text-sm">Today</button>
          </div>
          <div className="flex gap-1 rounded-lg bg-surface-gray p-1">
            {VIEWS.map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cx(
                  'rounded-md px-4 py-1.5 text-sm font-medium capitalize',
                  view === v ? 'bg-surface-card text-maroon-700 shadow-card' : 'text-ink-muted'
                )}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <PageLoader />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : (
          <>
            {view === 'month' && (
              <div className="card overflow-hidden">
                <div className="grid grid-cols-7 border-b border-cream-300 bg-surface-alt text-center text-xs font-semibold uppercase text-ink-muted">
                  {WEEKDAYS.map((d) => (
                    <div key={d} className="py-2">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {monthGrid.map((d, i) => {
                    const key = ymd(d);
                    const dayEntries = byDay[key] || [];
                    const isOtherMonth = d.getMonth() !== cursor.getMonth();
                    const isToday = key === ymd(new Date());
                    return (
                      <div
                        key={i}
                        className={cx(
                          'min-h-[90px] border-b border-r border-cream-300 p-1.5 text-sm',
                          isOtherMonth && 'bg-surface-alt text-ink-muted'
                        )}
                      >
                        <span className={cx('inline-flex h-6 w-6 items-center justify-center rounded-full text-xs', isToday && 'bg-maroon-700 text-white')}>
                          {d.getDate()}
                        </span>
                        <div className="mt-1 space-y-1">
                          {dayEntries.slice(0, 2).map((e) => (
                            <EntryWrapper
                              key={e.id}
                              entry={e}
                              title={e.title}
                              className={cx(
                                'block truncate rounded-full bg-gold-100 px-2 py-0.5 text-[10px] font-semibold text-maroon-900 transition-colors',
                                entryLink(e) && 'cursor-pointer hover:bg-gold-200 hover:underline'
                              )}
                            >
                              {e.title}
                            </EntryWrapper>
                          ))}
                          {dayEntries.length > 2 && (
                            <div className="text-[10px] text-ink-muted">+{dayEntries.length - 2} more</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {view === 'week' && (
              <div className="grid gap-3 sm:grid-cols-7">
                {weekDays.map((d) => {
                  const key = ymd(d);
                  const dayEntries = byDay[key] || [];
                  return (
                    <div key={key} className="card p-3">
                      <p className="mb-2 text-xs font-semibold text-ink-muted">
                        {d.toLocaleString('en-US', { weekday: 'short', day: 'numeric' })}
                      </p>
                      {dayEntries.length ? (
                        dayEntries.map((e) => (
                          <EntryWrapper
                            key={e.id}
                            entry={e}
                            title={e.title}
                            className={cx(
                              'mb-1 block rounded-full bg-maroon-50 px-2 py-1 text-xs font-semibold text-maroon-700 transition-colors',
                              entryLink(e) && 'cursor-pointer hover:bg-maroon-100 hover:underline'
                            )}
                          >
                            {e.title}
                          </EntryWrapper>
                        ))
                      ) : (
                        <p className="text-xs text-ink-muted">—</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {view === 'agenda' &&
              (entries.length ? (
                <div className="card divide-y divide-cream-300">
                  {[...entries]
                    .sort((a, b) => entryDate(a) - entryDate(b))
                    .map((e) => (
                      <EntryWrapper
                        key={e.id}
                        entry={e}
                        className={cx(
                          'flex items-start gap-4 p-4',
                          entryLink(e) && 'cursor-pointer transition-colors hover:bg-surface-alt'
                        )}
                      >
                        <div className="w-32 shrink-0 text-sm text-ink-muted">
                          {(() => {
                            const start = entryDate(e);
                            const endRaw = e.ends_at ? new Date(e.ends_at) : null;
                            const multiDay =
                              endRaw &&
                              !Number.isNaN(endRaw.getTime()) &&
                              ymd(endRaw) !== ymd(start);
                            return multiDay
                              ? `${formatDate(e.starts_at || e.date)} - ${formatDate(e.ends_at)}`
                              : formatDateTime(e.starts_at || e.date);
                          })()}
                        </div>
                        <div>
                          <p className={cx('font-medium text-ink', entryLink(e) && 'hover:underline')}>{e.title}</p>
                          {e.location && <p className="text-sm text-ink-muted">{e.location}</p>}
                        </div>
                      </EntryWrapper>
                    ))}
                </div>
              ) : (
                <EmptyState
                  icon="🗓"
                  title="No calendar entries"
                  message="There’s nothing scheduled for this view yet. Try another month or check back soon."
                />
              ))}
          </>
        )}
      </section>
    </>
  );
}
